import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";

/* map color names -> swatch hex */
const COLOR_SWATCHES = {
  "Auburn": "#8B3A2A",
  "Blonde": "#E8C98A",
  "Burgundy": "#5C1A2B",
  "Copper": "#B87333",
  "Dark Brown": "#3B2414",
  "Honey Blonde": "#D8A663",
  "Jet Black": "#0B0B0B",
  "Natural Black": "#1A1410",
  "Platinum": "#E8E6DF",
};

const Section = ({ title, children, testid }) => (
  <div data-testid={testid} className="py-5">
    <div className="text-xs uppercase tracking-[0.22em] text-foreground/70 font-semibold mb-4">
      {title}
    </div>
    {children}
  </div>
);

const Filters = ({
  filters,
  selected,
  onToggle,
  priceRange,
  priceBounds,
  onPriceChange,
  onClear,
  activeCount,
}) => {
  if (!filters) return <div className="text-sm text-muted-foreground p-4">Loading filters…</div>;

  return (
    <aside data-testid="filters-panel" className="w-full">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="font-serif text-2xl text-foreground">Filter</h3>
        {activeCount > 0 && (
          <Button
            data-testid="clear-all-filters"
            variant="ghost"
            size="sm"
            onClick={onClear}
            className="h-8 px-2 text-xs text-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]/80"
          >
            Clear all <X className="ml-1 w-3 h-3" />
          </Button>
        )}
      </div>

      {activeCount > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          <Badge variant="secondary" className="rounded-sm bg-secondary text-secondary-foreground">
            {activeCount} active
          </Badge>
        </div>
      )}

      <Separator className="bg-border/60" />

      {/* Length */}
      <Section title="Length" testid="filter-length">
        <div className="grid grid-cols-3 gap-2">
          {filters.lengths.map((l) => {
            const active = selected.lengths.includes(l.value);
            const empty = l.count === 0;
            return (
              <button
                key={l.value}
                data-testid={`filter-length-${l.value}`}
                onClick={() => onToggle("lengths", l.value)}
                title={empty ? "Coming soon" : `${l.count} available`}
                className={[
                  "relative h-10 text-sm font-medium rounded-sm border transition-all",
                  active
                    ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]"
                    : empty
                      ? "bg-white/50 border-border/50 text-muted-foreground/60 hover:border-foreground/30"
                      : "bg-white border-border/70 text-foreground/80 hover:border-foreground/40",
                ].join(" ")}
              >
                {l.value}
              </button>
            );
          })}
        </div>
      </Section>

      <Separator className="bg-border/60" />

      {/* Lace Size */}
      <Section title="Lace Size" testid="filter-lace-type">
        <div className="flex flex-col gap-3">
          {filters.lace_types.map((lt) => {
            const active = selected.laceTypes.includes(lt.value);
            return (
              <label
                key={lt.value}
                data-testid={`filter-lace-${lt.value}`}
                className="flex items-center justify-between cursor-pointer group"
              >
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={active}
                    onCheckedChange={() => onToggle("laceTypes", lt.value)}
                    className="border-foreground/40 data-[state=checked]:bg-[hsl(var(--primary))] data-[state=checked]:border-[hsl(var(--primary))]"
                  />
                  <span className={`text-sm ${active ? "text-foreground font-medium" : "text-foreground/80 group-hover:text-foreground"}`}>
                    {lt.value}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">{lt.count}</span>
              </label>
            );
          })}
        </div>
      </Section>

      <Separator className="bg-border/60" />

      {/* Color */}
      <Section title="Color" testid="filter-color">
        <div className="flex flex-col gap-3">
          {filters.colors.map((c) => {
            const active = selected.colors.includes(c.value);
            const swatch = COLOR_SWATCHES[c.value] || "#999";
            return (
              <label
                key={c.value}
                data-testid={`filter-color-${c.value}`}
                className="flex items-center justify-between cursor-pointer group"
              >
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={active}
                    onCheckedChange={() => onToggle("colors", c.value)}
                    className="border-foreground/40 data-[state=checked]:bg-[hsl(var(--primary))] data-[state=checked]:border-[hsl(var(--primary))]"
                  />
                  <span
                    className="w-4 h-4 rounded-full border border-border/80"
                    style={{ background: swatch }}
                    aria-hidden
                  />
                  <span className={`text-sm ${active ? "text-foreground font-medium" : "text-foreground/80 group-hover:text-foreground"}`}>
                    {c.value}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">{c.count}</span>
              </label>
            );
          })}
        </div>
      </Section>

      <Separator className="bg-border/60" />

      {/* Price */}
      <Section title="Price" testid="filter-price">
        <Slider
          data-testid="filter-price-slider"
          value={priceRange}
          min={priceBounds[0]}
          max={priceBounds[1]}
          step={1}
          onValueChange={onPriceChange}
          className="my-4"
        />
        <div className="flex items-center justify-between text-sm text-foreground/80 font-medium">
          <span data-testid="price-min-label">${priceRange[0]}</span>
          <span className="text-muted-foreground text-xs">to</span>
          <span data-testid="price-max-label">${priceRange[1]}</span>
        </div>
      </Section>
    </aside>
  );
};

export default Filters;
